import { CommonModule } from '@angular/common';
import { Component, inject, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';

import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MarkdownModule } from 'ngx-markdown';
import { MatInputModule } from '@angular/material/input';

import { environment } from '@environments/environment';
import Resources from '@constants/resourses';

import { RestapiService } from '@services/restapi.service';
import { SessionService } from '@services/session.service';
import { UtilityService } from '@services/utility.service';
import { TealiumUtagService } from '@services/utag.service';
import { GoogleAnalyticsService } from '@services/google-analytics.service';
import { IErrorHandling } from 'src/app/core/models/utility.interface';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MarkdownModule, ReactiveFormsModule, MatInputModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent implements OnInit, OnDestroy {
  abVariant: string;
  ctmFlowVariant: string;
  googleAnalytics = inject(GoogleAnalyticsService);
  emailId: string;
  maskedEmailId: string;
  subscriptions: Subscription[] = [];
  loginFormGroup: FormGroup;
  correlationId: string = '';

  id: string = '';
  title: string = '';
  content: string = '';

  tealiumData: any;

  isLoading: boolean = false;
  isSendLink: boolean = false;
  errorMessage: string;

  constructor(
    public dialogRef: MatDialogRef<any>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private formBuilder: FormBuilder,
    private restAPI: RestapiService,
    private utilityService: UtilityService,
    private sessionService: SessionService,
    private tealium: TealiumUtagService
  ) {
    this.emailId = this.sessionService.getItemFromLocal(Resources.SessionStorageKeys.Email);
    this.maskedEmailId = this.utilityService.maskingEmail(this.emailId);

    // Show content based on the user selection
    if (this.data?.target === 'login') {
      // login content
      this.subscriptions.push(
        this.utilityService.getMDFile('landingPage/landingPage-been-here-before.md').subscribe({
          next: (res) => {
            let splitData: any[] = res.split('\n');
            this.title = splitData[0];
            // Shift method to pop content from top
            splitData?.shift();
            // Rest of the content is join with new line
            this.content = splitData.join('\n');
          }, error: (err) => {
            console.error('Error fetching file:', err);
          }
        })
      );
    } else if (this.data?.target === 'energyResult') {
      this.correlationId = this.data?.correlationId || '';
      // energy result content
      this.title = 'Sign in to your energy result';
      this.content = 'To view your energy result, please sign in with your email address.';
    }
  }

  ngOnInit() {
    this.abVariant = this.utilityService.getCookie(Resources.ABtestingKeys.LANDING_PAGE_VARIANT_COOKIE) || 'A';
    this.ctmFlowVariant = this.utilityService.getCookie(Resources.ABtestingKeys.CTM_FLOW_VARIANT_COOKIE) || 'A';

    // Tealium data initialization
    this.tealiumData = {
      // app variables
      app_name: environment.tealium_app_name,
      app_version: environment.tealium_app_version,
      app_platform: environment.tealium_app_platform,

      // page variables
      page_name: 'BeenHereBeforeModal',
      page_type: 'RiskJourney',

      // Base URL
      base_url: window.location.href,
      energycomparison_subscribed: this.emailId ? true : false
    };

    // Trigger view event
    this.tealium.viewEvent(this.tealiumData);
    this.googleAnalytics.track({ abVariant: this.abVariant, ctmFlowVariant: this.ctmFlowVariant }, 'login_popup_open')

    if (this.data?.placement === 'header_sign_in') {
      // Call from the header sign in button
      if (window.location.pathname === '/home') {
        // Call from postcode entry page
        this.tealiumData['page_name'] = 'PostcodeEntry';
      } else if (window.location.pathname === '/information') {
        // Call from information page
        this.tealiumData['page_name'] = 'YourEnergyDashboard';
      } else if (window.location.pathname === '/energy-result') {
        // Call from energy result page
        this.tealiumData['page_name'] = 'YourTariffs';
      } else if (window.location.pathname === '/switch/user-details') {
        // Call from applciation page
        this.tealiumData['page_name'] = 'SwitchApplication';
      } else {
        // Call from switch confirmation page
        this.tealiumData['page_name'] = 'SwitchRequestConfirmed';
      }
      this.tealiumData['event_category'] = `Header`;
      this.tealiumData['event_label'] = 'SignIn';
    }

    // Trigger event tag
    this.tealium.linkEvent(this.tealiumData);

    // Ids for dom elements
    this.id = `${this.tealiumData?.app_name}_${this.tealiumData?.page_name}_BeenHereBefore`;

    // Initialization logic can go here
    this.loginFormGroup = this.formBuilder.group({
      email: [this.emailId || '', [Validators.required, Validators.pattern(Resources.Regex.Email)]]
    });

    // Validate the form on value change
    this.subscriptions.push(
      this.loginFormGroup.get('email')?.valueChanges.subscribe(() => {
        this.errorMessage = '';
      })
    );
  }

  // Tealium data setted
  tealiumDataSet(state: string): void {
    this.tealiumData['page_name'] = 'BeenHereBeforeModal';
    if (state === 'focus') {
      this.tealiumData['event_category'] = 'EmailEntry';
      this.tealiumData['event_label'] = 'EmailFieldEntry';
      this.tealiumData['event_action'] = 'onEntry';
    } else if (state === 'blur') {
      this.tealiumData['event_category'] = 'EmailExit';
      this.tealiumData['event_label'] = 'EmailFieldExit';
      this.tealiumData['event_action'] = 'onExit';
    }

    this.tealium.linkEvent(this.tealiumData);
  }

  // Method to close the dialog
  cancel(target: string): void {
    this.googleAnalytics.track({ abVariant: this.abVariant, ctmFlowVariant: this.ctmFlowVariant }, 'login_popup_cancel')

    if (target === 'beenHereBefore') {
      // Been here before popup
      // Event Added
      this.tealiumData['page_name'] = 'BeenHereBeforeModal';
      this.tealiumData['event_category'] = 'SendLink';
      this.tealiumData['event_label'] = 'Cancel';
      this.tealiumData['event_action'] = 'onClick';
    } else {
      // Check your inbox popup
      // Event Added
      this.tealiumData['page_name'] = 'CheckYourInboxModal';
      this.tealiumData['event_category'] = 'SendLink';
      this.tealiumData['event_label'] = 'Close';
      this.tealiumData['event_action'] = 'onClick';
    }

    // Trigger Event tag
    this.tealium.linkEvent(this.tealiumData);

    // trigger to close popup
    this.dialogRef.close();
  }

  // Validating form
  validateForm(target: string): void {
    if (target === 'beenHereBefore') {
      // Been here before popup
      if (this.loginFormGroup.valid) {
        const email = (this.loginFormGroup.get('email').value)?.toLowerCase()?.trim();
        if (this.correlationId) {
          // If correlationId is present, validate the email
          this.validateUserEmail(email);
        } else {
          // If correlationId is not present, proceed with login
          this.login(email);
        }
      } else {
        // Invalid email address
        this.tealiumData['page_name'] = 'BeenHereBeforeModal';
        this.tealiumData['event_category'] = 'SendLink';
        this.tealiumData['event_label'] = 'InvalidEmailAddress';
        this.tealiumData['event_action'] = 'onClick';

        // Trigger Event tag
        this.tealium.linkEvent(this.tealiumData);
        this.loginFormGroup.markAllAsTouched();
      }
    }
  }

  // [TEAM] login attempt [Acqisition Party Logo link is not from correct source, Error response with status code 200]
  login(email: string): void {
    this.googleAnalytics.track(
      {
        abVariant: this.abVariant,
        ctmFlowVariant: this.ctmFlowVariant,
        email: this.utilityService.getObfuscatedEmail(email)
      }, 'login_with_email'
    );

    this.isLoading = true;
    const payload = {
      email: email,
      subject: environment.subject,
      URL: `${window.location.origin}/login`,
      acqPartyDisplayName: 'CTM',
      acqPartyLogo: 'https://images.squarespace-cdn.com/content/v1/62fb3c8810f26a620ea606df/21fdce65-f4d5-4b7a-9c04-03f68233e34f/ctm-logo.png?format=1500w',
      correlationId: this.correlationId
    };

    this.subscriptions.push(
      this.restAPI.postLoginAPI('login', payload).subscribe({
        next: (res) => {
          // Event added
          this.tealiumData['customer_id'] = (!!email) ? this.utilityService.encryptIntoSha256(email) : undefined;
          this.tealiumData['event_category'] = 'SendLink';
          this.tealiumData['event_label'] = 'clicked';
          this.tealiumData['event_action'] = 'onClick';

          // Trigger Event tag
          this.tealium.linkEvent(this.tealiumData);

          // [TEAM] Error response with status code 200, it should be 400
          if (res?.errorDetail === 'User does not exist.') {

            // Event Added
            this.tealiumData['event_category'] = 'SendLink';
            this.tealiumData['event_label'] = 'UnrecognisedEmailAddress';
            this.tealiumData['event_action'] = 'onClick';

            // Trigger Event tag
            this.tealium.linkEvent(this.tealiumData);
            this.errorMessage = `We can’t recognise this email address. Check it's correct and try again or ‘Cancel’ and enter your postcode to get started.`
            this.isLoading = false;
            return;
          }

          // [TEAM] Content is getting from the response
          this.title = 'Check your inbox';
          this.content = res?.message;
          // Enable the check your inbox popup
          this.isSendLink = true;
          // Enable the loader
          this.isLoading = false;
        }, error: (err) => {
          // [TEAM] Error response with status code 200, it should be 400
          if (err?.error?.errorDetail === 'User does not exist.') {

            // Event Added
            this.tealiumData['customer_id'] = this.utilityService.encryptIntoSha256(email);
            this.tealiumData['event_category'] = 'SendLink';
            this.tealiumData['event_label'] = 'UnrecognisedEmailAddress';
            this.tealiumData['event_action'] = 'onClick';

            // Trigger Event tag
            this.tealium.linkEvent(this.tealiumData);
            this.errorMessage = `We can’t recognise this email address. Check it's correct and try again or ‘Cancel’ and enter your postcode to get started.`
          } else {
            this.errorMessage = err?.error?.errorDetail || 'Something went wrong. Please try again later.';
          }
          this.isLoading = false;
        }, complete: () => {
          // Store the email in local storage
          this.sessionService.setItemIntoLocal(Resources.SessionStorageKeys.Email, email);
        }
      })
    );
  }

  // Validate the email address
  validateUserEmail(email: string): void {
    const triggerTime = new Date().getTime();
    this.isLoading = true;
    this.subscriptions.push(
      this.sessionService.restoreSessionData(this.correlationId, email).subscribe({
        next: (res) => {
          // If email is found in session data, proceed with login
          if (!res?.email) {
            this.login(email);
          }
        }, error: (err) => {
          this.isLoading = true;
          const responseTime = new Date().getTime() - triggerTime;
          this.errorMessage = err?.error?.errorDetail || err?.error?.message || err?.error?.description || 'Something went wrong. Please try again later.';

          // Triggering an email to the team about session restore failure
          this.utilityService.sendAPIError('Session restore failed', { sessionData: { correlationId: this.correlationId, email } }, { sessionData: err }, responseTime);

          // Event Added
          this.tealiumData['event_label'] = '_SendLink_UnrecognisedEmailAddress';

          // Trigger Event tag
          this.tealium.linkEvent(this.tealiumData);
          this.isLoading = false;
          return;
        }
      })
    );
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions to prevent memory leaks
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }
}
